import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../../../lib/supabase";

interface FollowupSub {
  id: string;
  email: string;
  plan_id: string;
  welcome_email_sent_at: string | null;
  assigned_phone: string | null;
  followup_email_sent: boolean;
  setup_completed: boolean;
  status: string;
  preferred_lang: string | null;
}

type TriggerStatus = "idle" | "loading" | "success" | "error";

interface TriggerResult {
  sent?: number;
  failed?: number;
  total?: number;
  message?: string;
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function planLabel(planId: string) {
  const plans: Record<string, string> = { basico: "Básico", profesional: "Profesional", premium: "Premium" };
  return plans[planId?.toLowerCase()] ?? planId ?? "—";
}

const CRON_SQL = `-- 1. Habilitar extensiones (solo la primera vez)
CREATE EXTENSION IF NOT EXISTS pg_cron;
CREATE EXTENSION IF NOT EXISTS pg_net;

-- 2. Programar envío diario a las 9:00 AM UTC
SELECT cron.schedule(
  'send-followup-emails-daily',
  '0 9 * * *',
  $$
    SELECT net.http_post(
      url     := 'https://sdydqpjhqwlzaedmsncd.supabase.co/functions/v1/send-followup-email',
      headers := '{"Content-Type":"application/json","apikey":"sb_publishable_F_fEH7ST9rgR1hxvzvRPVA_fYpCkip6"}'::jsonb,
      body    := '{}'::jsonb,
      timeout_milliseconds := 30000
    );
  $$
);`;

export default function FollowupPanel() {
  const [subs, setSubs] = useState<FollowupSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggerStatus, setTriggerStatus] = useState<TriggerStatus>("idle");
  const [triggerResult, setTriggerResult] = useState<TriggerResult | null>(null);
  const [lastTriggered, setLastTriggered] = useState<string | null>(null);
  const [sqlCopied, setSqlCopied] = useState(false);
  const [showSqlBlock, setShowSqlBlock] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("subscriptions")
      .select("id, email, plan_id, welcome_email_sent_at, assigned_phone, followup_email_sent, setup_completed, status, preferred_lang")
      .eq("status", "active")
      .eq("setup_completed", false)
      .order("welcome_email_sent_at", { ascending: true });
    setSubs((data as FollowupSub[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
  const pendingNow = subs.filter(
    (s) => !s.followup_email_sent && s.welcome_email_sent_at && new Date(s.welcome_email_sent_at) <= threeDaysAgo
  );
  const alreadySent  = subs.filter((s) => s.followup_email_sent);
  const tooEarly     = subs.filter(
    (s) => !s.followup_email_sent && s.welcome_email_sent_at && new Date(s.welcome_email_sent_at) > threeDaysAgo
  );
  const noEmail      = subs.filter((s) => !s.followup_email_sent && !s.welcome_email_sent_at);

  const handleTrigger = async () => {
    setTriggerStatus("loading");
    setTriggerResult(null);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-followup-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({}),
        }
      );
      const data = await res.json();
      setTriggerStatus(data.success ? "success" : "error");
      setTriggerResult(data);
      setLastTriggered(new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }));
      if (data.success) fetchData();
    } catch {
      setTriggerStatus("error");
      setTriggerResult({ message: "Error de conexión" });
    }
    setTimeout(() => setTriggerStatus("idle"), 8000);
  };

  const copySQL = () => {
    navigator.clipboard.writeText(CRON_SQL);
    setSqlCopied(true);
    setTimeout(() => setSqlCopied(false), 3000);
  };

  return (
    <div className="space-y-4">

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { value: pendingNow.length, label: "Pendientes ahora", icon: "ri-mail-send-line", color: pendingNow.length > 0 ? "text-orange-600" : "text-stone-400", bg: pendingNow.length > 0 ? "bg-orange-50" : "bg-stone-50", urgent: pendingNow.length > 0 },
          { value: tooEarly.length, label: "Esperando 3 días", icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50", urgent: false },
          { value: alreadySent.length, label: "Enviados (setup pendiente)", icon: "ri-check-double-line", color: "text-emerald-600", bg: "bg-emerald-50", urgent: false },
          { value: noEmail.length, label: "Sin email de bienvenida", icon: "ri-error-warning-line", color: noEmail.length > 0 ? "text-rose-500" : "text-stone-400", bg: noEmail.length > 0 ? "bg-rose-50" : "bg-stone-50", urgent: false },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-stone-100 rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg}`}>
              <i className={`${s.icon} ${s.color} text-lg`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${s.urgent ? s.color : "text-stone-900"}`}>{s.value}</p>
              <p className="text-xs text-stone-400 font-medium leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Automation + trigger card */}
      <div className="grid grid-cols-5 gap-4">

        {/* Cron setup */}
        <div className="col-span-3 bg-white border border-stone-100 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
                <i className="ri-timer-line text-stone-400" />
                Automatización diaria (pg_cron)
              </h2>
              <p className="text-xs text-stone-400 mt-0.5">Envío automático cada día a las 9:00 AM — configúralo una sola vez</p>
            </div>
            <button
              onClick={() => setShowSqlBlock((v) => !v)}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap ${showSqlBlock ? "bg-stone-900 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}
            >
              <i className={`ri-code-s-slash-line`} />
              {showSqlBlock ? "Ocultar SQL" : "Ver SQL"}
            </button>
          </div>

          {showSqlBlock && (
            <div className="px-6 py-4 border-b border-stone-100 bg-stone-50">
              <p className="text-xs text-stone-600 mb-3">
                Pega este SQL en{" "}
                <strong>Supabase Dashboard → SQL Editor → New query</strong>{" "}
                y ejecútalo <span className="text-orange-600 font-semibold">una sola vez</span>. A partir de entonces el seguimiento se dispara automáticamente cada día.
              </p>
              <div className="relative">
                <pre className="bg-stone-900 text-green-400 text-xs rounded-xl p-4 overflow-x-auto leading-relaxed font-mono whitespace-pre">{CRON_SQL}</pre>
                <button
                  onClick={copySQL}
                  className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {sqlCopied ? <><i className="ri-check-line" /> Copiado</> : <><i className="ri-clipboard-line" /> Copiar</>}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { icon: "ri-link", label: "Supabase Dashboard", href: "https://supabase.com/dashboard" },
                  { icon: "ri-information-line", label: "Docs pg_cron", href: "https://supabase.com/docs/guides/database/extensions/pg_cron" },
                ].map((l) => (
                  <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                    <i className={`${l.icon} text-xs`} />
                    {l.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="px-6 py-4 space-y-3">
            {[
              { icon: "ri-mail-send-line", label: "¿Qué envía?", desc: "El email de seguimiento del día 3 a todos los clientes activos con setup pendiente que aún no lo han recibido." },
              { icon: "ri-shield-check-line", label: "¿Envía duplicados?", desc: "No. Una vez enviado, marca followup_email_sent = true y no vuelve a tocar esa suscripción." },
              { icon: "ri-flag-line", label: "Horario", desc: "9:00 AM UTC (10h en invierno español, 11h en verano). Ajústalo en el cron si prefieres otro horario." },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-stone-50 border border-stone-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-stone-500 text-xs`} />
                </div>
                <div>
                  <p className="text-xs font-bold text-stone-700">{item.label}</p>
                  <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual trigger */}
        <div className="col-span-2 bg-white border border-stone-100 rounded-2xl p-6 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2 mb-1">
              <i className="ri-play-circle-line text-stone-400" />
              Disparar ahora manualmente
            </h2>
            <p className="text-xs text-stone-400 leading-relaxed">
              Procesa todos los follow-ups pendientes en este momento. Útil para pruebas o cuando necesitas enviar sin esperar al cron.
            </p>

            {pendingNow.length > 0 && (
              <div className="mt-3 px-3 py-2.5 bg-orange-50 border border-orange-100 rounded-xl">
                <p className="text-xs font-semibold text-orange-700">
                  <i className="ri-mail-send-line mr-1" />
                  {pendingNow.length} email{pendingNow.length !== 1 ? "s" : ""} listo{pendingNow.length !== 1 ? "s" : ""} para enviar
                </p>
              </div>
            )}

            {pendingNow.length === 0 && (
              <div className="mt-3 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                <p className="text-xs font-semibold text-emerald-700">
                  <i className="ri-check-line mr-1" />
                  Sin pendientes en este momento
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <button
              onClick={handleTrigger}
              disabled={triggerStatus === "loading"}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-stone-900 hover:bg-stone-700 disabled:bg-stone-200 disabled:text-stone-400 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {triggerStatus === "loading" ? (
                <><i className="ri-loader-4-line animate-spin" /> Procesando...</>
              ) : (
                <><i className="ri-send-plane-line" /> Disparar seguimiento</>
              )}
            </button>

            {triggerStatus === "success" && triggerResult && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
                <i className="ri-check-double-line text-emerald-500 text-sm" />
                <p className="text-xs font-semibold text-emerald-700">
                  {triggerResult.sent === 0
                    ? (triggerResult.message ?? "Sin pendientes")
                    : `${triggerResult.sent} enviado${(triggerResult.sent ?? 0) > 1 ? "s" : ""}${(triggerResult.failed ?? 0) > 0 ? ` · ${triggerResult.failed} fallidos` : ""}`}
                  {lastTriggered && <span className="text-emerald-500 ml-1">· {lastTriggered}</span>}
                </p>
              </div>
            )}

            {triggerStatus === "error" && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-rose-50 border border-rose-100 rounded-xl">
                <i className="ri-error-warning-line text-rose-500 text-sm" />
                <p className="text-xs font-semibold text-rose-700">{triggerResult?.message ?? "Error al procesar"}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending list */}
      <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-stone-900">Clientes con setup pendiente</h2>
            <p className="text-xs text-stone-400">{subs.length} en total · activos y sin completar configuración</p>
          </div>
          <button onClick={fetchData} className="w-9 h-9 flex items-center justify-center rounded-xl border border-stone-200 text-stone-500 hover:bg-stone-50 transition-colors cursor-pointer">
            <i className="ri-refresh-line text-sm" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 gap-3 text-stone-400">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm">Cargando...</span>
          </div>
        ) : subs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-stone-400">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-2xl mb-3">
              <i className="ri-check-double-line text-2xl text-emerald-500" />
            </div>
            <p className="text-sm font-medium text-emerald-600">Todos los clientes han completado el setup</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-100">
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Cliente</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Plan</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Número</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Bienvenida enviada</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Días desde bienvenida</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Seguimiento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {subs.map((sub) => {
                  const days = daysSince(sub.welcome_email_sent_at);
                  const isDue = !sub.followup_email_sent && days !== null && days >= 3;
                  const noWelcome = !sub.welcome_email_sent_at;
                  return (
                    <tr key={sub.id} className={`transition-colors ${isDue ? "bg-orange-50/30 hover:bg-orange-50/60" : "hover:bg-stone-50"}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-stone-400 text-sm" />
                          </div>
                          <span className="text-sm text-stone-700 font-medium truncate max-w-[180px]">{sub.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 bg-stone-900 text-white text-xs rounded-lg font-bold whitespace-nowrap">
                          {planLabel(sub.plan_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {sub.assigned_phone ? (
                          <span className="font-mono text-xs font-bold text-stone-800 bg-stone-100 px-2 py-1 rounded-lg">{sub.assigned_phone}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg font-medium">
                            <i className="ri-phone-off-line text-xs" /> Sin número
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-500 whitespace-nowrap">
                        {noWelcome ? (
                          <span className="text-xs text-rose-500 font-medium flex items-center gap-1">
                            <i className="ri-close-circle-line" /> No enviado
                          </span>
                        ) : formatDate(sub.welcome_email_sent_at)}
                      </td>
                      <td className="px-6 py-4">
                        {days === null ? (
                          <span className="text-xs text-stone-400">—</span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                            days >= 3
                              ? "bg-orange-100 text-orange-700"
                              : "bg-stone-100 text-stone-500"
                          }`}>
                            {days >= 3 && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0 animate-pulse" />}
                            {days}d
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {sub.followup_email_sent ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg font-medium">
                            <i className="ri-check-line text-xs" /> Enviado
                          </span>
                        ) : isDue ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-700 text-xs rounded-lg font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
                            Pendiente de enviar
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-50 text-stone-500 text-xs rounded-lg font-medium">
                            <i className="ri-time-line text-xs" /> Esperando {days !== null ? `${3 - days}d más` : "bienvenida"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
